/**
 * /pipeline-facility/sites/:id — one pipeline or facility
 * (GET / PATCH / DELETE /pipeline-facility/sites/:id, fieldiq-api#1196).
 *
 * Panels stack full width: details on top, the edit form below them when
 * editing (never a modal), then a pipeline's segments. A facility has no
 * segments section at all — the API refuses a segment on one.
 *
 * Edit and delete are PF_ADMIN (admin, office_staff); segment writes add the
 * supervisor. Any other role reads. A refusal that still arrives is shown as a
 * refusal in the form, never as a green toast. Delete asks in-app first.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Activity, GitCommitHorizontal, Gauge, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDeletePfSite, usePfCustomers, usePfMeta, usePfSite, useUpdatePfSite } from '../../hooks/usePipelineFacility';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminPf, canDispatchPf, canWriteSegments, fmtNumber, pfErrorMessage, pfStatusSkin, withUnit } from '../../lib/pipeline-facility';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';
import { SegmentsPanel } from './SegmentsPanel';
import { PfSiteAnomaliesPanel, PfSiteDigHistory } from './digs/JobDigSections';
import { SiteForm } from './SiteForm';
import { isNotFound, PF_EYEBROW, PF_PRIMARY, PF_SECONDARY, PfBadge, PfDialog, PfEmpty, PfFormError, PfLoading, PfPanel, PfSpec } from './PfBits';

export default function PfSiteDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/pipeline-facility/sites');
  const { user } = useAuth();
  const query = usePfSite(id);
  const catalog = usePfMeta().data?.data ?? null;
  const canEdit = canAdminPf(user?.role);
  const customers = usePfCustomers(canEdit);
  const update = useUpdatePfSite();
  const del = useDeletePfSite();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const site = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const u = meta?.units ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const isPipeline = site?.kind === 'pipeline';
  const segments = site?.segments ?? [];
  const kindLabel = site ? site.kindLabel ?? site.kind : null;

  return (
    <div data-testid="pf-site-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={kindLabel ? `${PF_EYEBROW} · ${kindLabel}` : PF_EYEBROW}
        title={site?.name || (query.isLoading ? 'Site' : '—')}
        onBack={back}
        backLabel="Back"
        actions={
          site && canEdit ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="fi-action fi-action--standard fi-action--primary"
                onClick={() => { setEditError(null); setEditing(true); }}
                disabled={editing}
              >
                <Pencil className="size-4" aria-hidden="true" />
                Edit site
              </button>
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
        stats={[
          {
            icon: <Activity className="size-4" aria-hidden="true" />,
            label: 'Status',
            tone: site?.status === 'active' ? 'success' : 'neutral',
            loading: query.isLoading,
            value: site ? pfStatusSkin(site.status).label : '—',
          },
          isPipeline || !site
            ? {
                icon: <GitCommitHorizontal className="size-4" aria-hidden="true" />,
                label: 'Segments',
                tone: 'neutral',
                loading: query.isLoading,
                value: site ? displayValue(segments.length) : '—',
              }
            : {
                icon: <GitCommitHorizontal className="size-4" aria-hidden="true" />,
                label: 'Type',
                tone: 'neutral',
                loading: query.isLoading,
                value: site.facilityType || '—',
              },
          {
            icon: <Gauge className="size-4" aria-hidden="true" />,
            label: 'MOP',
            tone: 'neutral',
            loading: query.isLoading,
            value: withUnit(site?.mop, u?.pressure),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <PfLoading label="Loading site" />
          ) : notFound || (!failure && !site) ? (
            <PfPanel eyebrow="Site" title="Site">
              <PfEmpty>This site was not found. It may have been deleted.</PfEmpty>
            </PfPanel>
          ) : failure || !site ? (
            <PfPanel eyebrow="Site" title="Site">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this site" />
            </PfPanel>
          ) : (
            <>
              <PfPanel eyebrow={kindLabel ?? 'Site'} title="Details" testId="pf-site-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <PfSpec label="Customer" value={site.customerName || '—'} />
                  <PfSpec label="Kind" value={kindLabel || '—'} />
                  <PfSpec label="Status" value={<PfBadge skin={pfStatusSkin(site.status)} value={site.status} />} />
                  <PfSpec label="Licence number" value={site.licenceNumber || '—'} mono />
                  {isPipeline ? (
                    <PfSpec label="Line number" value={site.lineNumber || '—'} mono />
                  ) : (
                    <PfSpec label="Facility type" value={site.facilityType || '—'} />
                  )}
                  <PfSpec label="LSD / location" value={site.lsd || '—'} />
                  <PfSpec label="Province / state" value={site.provinceName || site.provinceState || '—'} />
                  <PfSpec
                    label="Coordinates"
                    value={site.latitude != null && site.longitude != null ? `${fmtNumber(site.latitude)}, ${fmtNumber(site.longitude)}` : '—'}
                    mono
                  />
                  <PfSpec label="Product" value={site.productLabel || '—'} />
                  <PfSpec label="Service" value={site.sour ? 'Sour (H2S)' : 'Sweet'} />
                  {isPipeline && (
                    <>
                      <PfSpec label="OD" value={withUnit(site.od, u?.diameter)} mono />
                      <PfSpec label="WT" value={withUnit(site.wt, u?.wallThickness)} mono />
                      <PfSpec label="Grade" value={site.grade || '—'} />
                      <PfSpec label="MOP" value={withUnit(site.mop, u?.pressure)} mono />
                    </>
                  )}
                </dl>
                {site.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{site.notes}</p>}
              </PfPanel>

              {editing && canEdit && (
                <PfPanel eyebrow="Edit" title="Edit site" testId="pf-site-edit">
                  <SiteForm
                    site={site}
                    catalog={catalog}
                    meta={meta}
                    customers={customers.data ?? []}
                    pending={update.isPending}
                    error={editError}
                    submitLabel="Save site"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) =>
                      update.mutate(
                        { id: site.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Site saved');
                            setEditing(false);
                          },
                          onError: (err) => setEditError(pfErrorMessage(err, 'The site could not be saved.')),
                        },
                      )
                    }
                  />
                </PfPanel>
              )}

              {isPipeline && <SegmentsPanel siteId={site.id} segments={segments} meta={meta} canWrite={canWriteSegments(user?.role)} />}
              {isPipeline && <PfSiteAnomaliesPanel siteId={site.id} canImport={canDispatchPf(user?.role)} />}
              <PfSiteDigHistory siteId={site.id} />
            </>
          )}
        </div>
      </section>

      {site && canEdit && (
        <PfDialog
          open={deleting}
          title="Delete site"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={PF_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep site
              </button>
              <button
                type="button"
                className={PF_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(site.id, {
                    onSuccess: () => {
                      toast.success('Site deleted');
                      navigate('/pipeline-facility/sites', { replace: true });
                    },
                    onError: (err) => setDeleteError(pfErrorMessage(err, 'The site could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete site'}
              </button>
            </>
          }
        >
          <PfFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">
            {isPipeline && segments.length > 0
              ? `Removes ${site.name} and its ${segments.length} segment${segments.length === 1 ? '' : 's'} from the register.`
              : `Removes ${site.name} from the register.`}
          </p>
        </PfDialog>
      )}
    </div>
  );
}
